/* Hexdeck – Trade binder ("Sell your cards").
   Card data is rendered by Shopify (templates/page.binder-cards.liquid) and loaded as JSON.
   The binder is stored in localStorage; the offer is sent through Shopify's contact form, so it
   arrives as a normal store email with the full card list. Offer per card:
   market price × buy % (standard / overnumbered / signature) × condition factor, +bonus for store credit. */
(() => {
  const root = document.querySelector('[data-trade-binder]');
  if (!root) return;

  const H = window.Hexdeck || {};
  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => Array.from(r.querySelectorAll(s));
  const S = JSON.parse($('[data-tb-strings]').textContent);
  const num = (k, d) => { const v = parseFloat(root.dataset[k]); return Number.isFinite(v) ? v : d; };
  const cfg = {
    url: root.dataset.url,
    pct: { standard: num('pctStandard', 70), overnumbered: num('pctOvernumbered', 80), signature: num('pctSignature', 90) },
    bonus: num('creditBonus', 10),
    cond: { NM: 0, EX: num('condEx', 10), GD: num('condGd', 25), PL: num('condPl', 40) }
  };
  const CONDS = ['NM', 'EX', 'GD', 'PL'];
  const STORAGE_KEY = 'hexdeck.binder.v1';
  const PAGE_SIZE = 48;
  const BODY_LIMIT = 15000; // keep the email a sensible size

  /* ---------------------------------------------------------------- helpers */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fmt = (s, vars = {}) => String(s).replace(/%(\w+)%/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const euro = (v) => '€' + (Math.round(v * 100) / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const live = (msg) => { const el = $('[data-tb-live]'); if (el) { el.textContent = ''; requestAnimationFrame(() => { el.textContent = msg; }); } };
  const toast = (msg, type) => (H.toast ? H.toast(msg, type) : live(msg));
  const openDialog = (el, opener) => (H.openDialog ? H.openDialog(el.id, opener) : el.showModal());

  const state = { cards: [], byKey: new Map(), filtered: [], rendered: 0, f: { q: '', s: '', r: '', t: '', b: '', mine: false }, sort: 'value', store: { items: {}, payout: 'cash' } };

  /* ------------------------------------------------------------------ store */
  // items: { "<productId>|<condition>": quantity }
  function loadStore() {
    try { const raw = localStorage.getItem(STORAGE_KEY); if (raw) state.store = { items: {}, payout: 'cash', ...JSON.parse(raw) }; } catch (_) { /* storage unavailable */ }
  }
  const saveLocal = debounce(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.store)); } catch (_) { /* ignore */ } }, 150);
  const saveStore = () => { saveLocal(); syncAccount(); };

  /* -------------------------------------------------------- account sync */
  // Logged-in customers: the binder is also saved on their account through /apps/binder (Shopify app
  // proxy → binder service → customer metafield hexdeck.binder), so it follows them to every device.
  const customerId = root.dataset.customer;
  function accountStatus(stateName, text) {
    const el = $('[data-tb-account]');
    if (!el) return;
    el.dataset.state = stateName;
    $('[data-tb-account-text]').textContent = text;
  }
  const syncAccount = debounce(async () => {
    if (!customerId) return;
    accountStatus('saving', S.accountSaving);
    try {
      const res = await fetch(root.dataset.syncUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: state.store.items, payout: state.store.payout })
      });
      if (!res.ok) throw new Error(String(res.status));
      accountStatus('saved', S.accountSaved);
    } catch (_) {
      accountStatus('error', S.accountError);
    }
  }, 1200);

  // On load: an account binder with cards wins (it's the customer's latest, from any device);
  // an empty account gets this device's binder uploaded.
  function mergeAccountBinder() {
    const el = $('[data-tb-account-binder]');
    if (!customerId || !el) return;
    let saved = null;
    try { saved = JSON.parse(el.textContent); } catch (_) { /* no saved binder */ }
    const hasSaved = saved && saved.items && Object.keys(saved.items).length > 0;
    if (hasSaved) {
      const same = JSON.stringify(saved.items) === JSON.stringify(state.store.items);
      state.store.items = { ...saved.items };
      state.store.payout = saved.payout === 'credit' ? 'credit' : 'cash';
      saveLocal();
      if (!same) accountStatus('saved', S.accountLoaded);
    } else if (Object.keys(state.store.items).length) {
      syncAccount();
    }
  }
  const qtyOf = (k) => CONDS.reduce((n, c) => n + (state.store.items[`${k}|${c}`] || 0), 0);

  /* ----------------------------------------------------------------- prices */
  // Offer for one copy, in euros (rounded down to the cent). null = no market price: priced on request.
  function unitOffer(card, cond = 'NM', credit = false) {
    if (card.mp === null || card.mp === undefined) return null;
    const pct = cfg.pct[card.b] ?? cfg.pct.standard;
    let v = card.mp * (pct / 100) * (1 - (cfg.cond[cond] || 0) / 100);
    if (credit) v *= 1 + cfg.bonus / 100;
    return Math.floor(v * 100 + 1e-6) / 100;
  }

  const isCredit = () => state.store.payout === 'credit';

  function lines() {
    return Object.entries(state.store.items)
      .filter(([, q]) => q > 0)
      .map(([key, qty]) => {
        const [k, cond] = key.split('|');
        const card = state.byKey.get(k);
        return card ? { key, card, cond, qty } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (unitOffer(b.card, b.cond) ?? -1) - (unitOffer(a.card, a.cond) ?? -1) || a.card.n.localeCompare(b.card.n));
  }

  function totals() {
    let units = 0, cash = 0, credit = 0, onRequest = 0;
    lines().forEach((l) => {
      units += l.qty;
      const c = unitOffer(l.card, l.cond);
      if (c === null) onRequest += l.qty;
      else { cash += c * l.qty; credit += unitOffer(l.card, l.cond, true) * l.qty; }
    });
    return { units, cash, credit, onRequest };
  }

  /* ------------------------------------------------------------------- data */
  async function fetchPage(n) {
    // No JSON Accept header: Shopify would answer with its own page JSON instead of our template.
    const res = await fetch(`${cfg.url}&page=${n}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    return JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
  }

  async function load() {
    $('[data-tb-error]').hidden = true;
    $('[data-tb-grid]').setAttribute('aria-busy', 'true');
    try {
      const first = await fetchPage(1);
      const rest = [];
      for (let i = 2; i <= first.pages; i += 1) rest.push(fetchPage(i));
      const pages = [first, ...(await Promise.all(rest))];
      const setOrder = new Map();
      state.cards = pages.flatMap((p) => p.cards || []).map((c, i) => {
        const card = { ...c, k: String(c.k), i, mp: typeof c.mp === 'number' ? c.mp : (c.mp ? parseFloat(c.mp) : null) };
        if (!setOrder.has(card.s)) setOrder.set(card.s, setOrder.size);
        card.blob = norm([card.n, card.c, card.s, card.r, card.t, S[`kind_${card.b}`]].join(' '));
        return card;
      });
      state.byKey = new Map(state.cards.map((c) => [c.k, c]));
      buildFilterOptions();
      applyFilters();
      renderBinder();
    } catch (err) {
      $('[data-tb-grid]').innerHTML = '';
      $('[data-tb-error]').hidden = false;
    } finally {
      $('[data-tb-grid]').setAttribute('aria-busy', 'false');
    }
  }

  function buildFilterOptions() {
    const fill = (key) => {
      const sel = $(`[data-tb-filter="${key}"]`);
      const values = [...new Set(state.cards.map((c) => c[key]).filter(Boolean))];
      if (key !== 's') values.sort();
      sel.insertAdjacentHTML('beforeend', values.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join(''));
    };
    ['s', 'r', 't'].forEach(fill);
  }

  /* --------------------------------------------------------------- browsing */
  function applyFilters() {
    const f = state.f;
    const words = norm(f.q).split(/\s+/).filter(Boolean);
    state.filtered = state.cards.filter((c) => (!f.s || c.s === f.s) && (!f.r || c.r === f.r) && (!f.t || c.t === f.t)
      && (!f.b || c.b === f.b) && (!f.mine || qtyOf(c.k) > 0) && words.every((w) => c.blob.includes(w)));
    const by = {
      value: (a, b) => (b.mp ?? -1) * (cfg.pct[b.b] || 0) - (a.mp ?? -1) * (cfg.pct[a.b] || 0) || a.i - b.i,
      set: (a, b) => a.i - b.i,
      name: (a, b) => a.n.localeCompare(b.n)
    };
    state.filtered.sort(by[state.sort] || by.value);
    state.rendered = 0;
    $('[data-tb-grid]').innerHTML = '';
    renderMore();
    $('[data-tb-empty]').hidden = state.filtered.length > 0;
    $('[data-tb-count]').textContent = fmt(S.cardsShown, { count: state.filtered.length, total: state.cards.length });
  }

  function tileHTML(c) {
    const qty = qtyOf(c.k);
    const offer = unitOffer(c, 'NM', isCredit());
    const img = c.img ? `<img src="${esc(c.img)}" alt="" loading="lazy" decoding="async" width="240" height="335">` : `<span class="db-noimg"><span>${esc(c.n)}</span></span>`;
    const badge = c.b !== 'standard' ? `<span class="tb-kind tb-kind--${esc(c.b)}">${esc(S[`kind_${c.b}`])}</span>` : '';
    return `
      <div class="db-tile tb-tile${qty ? ' is-in-deck' : ''}" data-k="${esc(c.k)}">
        <a class="db-tile__img" href="${esc(c.u)}" target="_blank" rel="noopener" aria-label="${esc(c.n)}">${img}</a>
        <span class="db-tile__qty" ${qty ? '' : 'hidden'}>${qty}×</span>
        <div class="db-tile__info">
          <span class="db-tile__name" title="${esc(c.n)}">${esc(c.n)}</span>
          <span class="db-tile__meta"><span class="db-tile__id">${esc(c.c)}</span>${badge}</span>
          <span class="tb-offer">${offer === null ? `<span class="muted">${esc(S.onRequest)}</span>` : `${esc(S.weBuy)} <b>${euro(offer)}</b>`}</span>
        </div>
        <div class="db-tile__actions">
          <button type="button" class="db-btn" data-dec aria-label="${esc(S.remove)}: ${esc(c.n)}" ${qty ? '' : 'disabled'}><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
          <button type="button" class="db-btn db-btn--add" data-inc aria-label="${esc(S.add)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
        </div>
      </div>`;
  }

  function renderMore() {
    const next = state.filtered.slice(state.rendered, state.rendered + PAGE_SIZE);
    if (!next.length) return;
    $('[data-tb-grid]').insertAdjacentHTML('beforeend', next.map(tileHTML).join(''));
    state.rendered += next.length;
  }

  function refreshTile(k) {
    const el = root.querySelector(`.tb-tile[data-k="${CSS.escape(k)}"]`);
    const c = state.byKey.get(k);
    if (el && c) el.outerHTML = tileHTML(c);
  }

  /* ----------------------------------------------------------------- binder */
  function change(k, cond, delta) {
    const key = `${k}|${cond}`;
    const q = Math.max(0, (state.store.items[key] || 0) + delta);
    if (q) state.store.items[key] = q; else delete state.store.items[key];
    saveStore();
    refreshTile(k);
    renderBinder();
  }

  // Grid "−" removes the lowest-condition copy first, so NM copies stay.
  function decrement(k) {
    const cond = [...CONDS].reverse().find((c) => state.store.items[`${k}|${c}`] > 0);
    if (cond) change(k, cond, -1);
  }

  function setCondition(key, cond) {
    const [k, from] = key.split('|');
    if (from === cond) return;
    const q = state.store.items[key] || 0;
    delete state.store.items[key];
    state.store.items[`${k}|${cond}`] = (state.store.items[`${k}|${cond}`] || 0) + q;
    saveStore();
    renderBinder();
  }

  function lineHTML(l) {
    const c = l.card;
    const unit = unitOffer(c, l.cond, isCredit());
    return `
      <li class="db-line tb-line" data-key="${esc(l.key)}">
        <a class="db-line__thumb" href="${esc(c.u)}" target="_blank" rel="noopener" aria-label="${esc(c.n)}">${c.img ? `<img src="${esc(c.img)}" alt="" loading="lazy" width="40" height="56">` : ''}</a>
        <div class="db-line__main">
          <span class="db-line__name" title="${esc(c.n)}">${esc(c.n)}</span>
          <span class="tb-line__meta">
            <label class="visually-hidden" for="cond-${esc(l.key)}">${esc(S.condition)}</label>
            <select class="tb-cond" id="cond-${esc(l.key)}" data-cond>${CONDS.map((x) => `<option value="${x}" ${x === l.cond ? 'selected' : ''}>${esc(S[`cond${x}`])}</option>`).join('')}</select>
            <span>${unit === null ? esc(S.onRequest) : `${euro(unit)} ${esc(S.each || '')}`}</span>
          </span>
        </div>
        <div class="db-line__qty">
          <button type="button" class="db-btn db-btn--sm" data-line-dec aria-label="${esc(S.remove)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
          <span>${l.qty}</span>
          <button type="button" class="db-btn db-btn--sm" data-line-inc aria-label="${esc(S.add)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
        </div>
        <span class="db-line__price">${unit === null ? '–' : euro(unit * l.qty)}</span>
      </li>`;
  }

  function renderBinder() {
    const ls = lines();
    const t = totals();
    const list = $('[data-tb-list]');
    if (!ls.length) list.innerHTML = list.dataset.emptyHtml;
    else list.innerHTML = `<ul class="tb-lines">${ls.map(lineHTML).join('')}</ul>`;

    $('[data-tb-total-cash]').textContent = euro(t.cash);
    $('[data-tb-total-credit]').textContent = euro(t.credit);
    const credit = state.store.payout === 'credit';
    $$('[data-tb-payout]').forEach((r) => { r.checked = r.value === state.store.payout; });
    $('[data-tb-summary]').innerHTML = t.units ? `
      <div class="cart-summary__row"><span>${esc(S.cardCount)}</span><span><b>${t.units}</b></span></div>
      ${t.onRequest ? `<div class="cart-summary__row muted"><span>${esc(S.onRequestCount)}</span><span>${t.onRequest}</span></div>` : ''}
      <div class="cart-summary__row cart-summary__total"><span>${esc(credit ? S.creditTotal : S.cashTotal)}</span><span>${euro(credit ? t.credit : t.cash)}</span></div>` : '';
    $('[data-tb-send]').disabled = !t.units;
    $('[data-tb-bar-count]').textContent = t.units;
    $('[data-tb-bar-sub]').textContent = t.units ? `${euro(credit ? t.credit : t.cash)} · ${credit ? S.payoutCredit : S.payoutCash}` : S.empty;
  }

  /* ------------------------------------------------------------------ offer */
  const newRef = () => `HX-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  function offerText(ref) {
    const t = totals();
    const credit = state.store.payout === 'credit';
    const out = [
      `Trade binder offer ${ref}`,
      `Payout: ${credit ? `Store credit (+${cfg.bonus}%)` : 'Bank transfer'}`,
      `Offer total: ${euro(credit ? t.credit : t.cash)}${credit ? ` (cash value ${euro(t.cash)})` : ''}`,
      `Cards: ${t.units}${t.onRequest ? ` (${t.onRequest} without market price: price on request)` : ''}`,
      `Rates: ${cfg.pct.standard}% regular / ${cfg.pct.overnumbered}% overnumbered / ${cfg.pct.signature}% signature of market price; EX -${cfg.cond.EX}%, GD -${cfg.cond.GD}%, PL -${cfg.cond.PL}%`,
      ''
    ];
    lines().forEach((l) => {
      const u = unitOffer(l.card, l.cond, credit);
      out.push(`${l.qty}x ${l.card.n} [${l.card.c}] ${l.cond} · ${u === null ? 'price on request' : `${euro(u)} each = ${euro(u * l.qty)}`}${l.card.mp != null ? ` (market ${euro(l.card.mp)})` : ''}`);
    });
    return out.join('\n');
  }

  // Everything the printable receipt needs, saved when the send form opens (the page reloads after sending).
  function offerData(ref) {
    const t = totals();
    const credit = state.store.payout === 'credit';
    return {
      ref, at: Date.now(), credit, bonus: cfg.bonus, pct: cfg.pct, cond: cfg.cond,
      units: t.units, onRequest: t.onRequest, cash: t.cash, creditTotal: t.credit,
      lines: lines().map((l) => ({
        qty: l.qty, name: l.card.n, code: l.card.c, cond: l.cond, kind: l.card.b || 'standard',
        unit: unitOffer(l.card, l.cond, credit), img: l.card.img || ''
      }))
    };
  }

  function exportText() {
    return lines().map((l) => `${l.qty} ${l.card.c}${l.card.b === 'signature' ? ' Signature' : ''} ${l.cond === 'NM' ? '' : l.cond} # ${l.card.n}`.replace(/\s+#/, ' #')).join('\n');
  }

  function openSend(opener) {
    const ref = state.store.pendingRef || newRef();
    state.store.pendingRef = ref;
    saveStore();
    const t = totals();
    const credit = state.store.payout === 'credit';
    const body = offerText(ref);
    state.store.pendingOffer = body; // kept on this device for the "Print / save offer" receipt
    state.store.pendingData = offerData(ref);
    saveLocal();
    $('[data-tb-ref-field]').value = ref;
    $('[data-tb-payout-field]').value = credit ? `Store credit (+${cfg.bonus}%)` : 'Bank transfer';
    $('[data-tb-total-field]').value = euro(credit ? t.credit : t.cash);
    $('[data-tb-body-field]').value = body.length > BODY_LIMIT ? body.slice(0, BODY_LIMIT) + '\n…' : body;
    $('[data-tb-send-summary]').innerHTML = `
      <div><span>${esc(S.cardCount)}</span><b>${t.units}</b></div>
      <div><span>${esc(credit ? S.payoutCredit : S.payoutCash)}</span><b>${euro(credit ? t.credit : t.cash)}</b></div>
      <div><span>Ref.</span><b>${esc(ref)}</b></div>
      ${body.length > BODY_LIMIT ? `<p class="small">${esc(S.tooLong)}</p>` : ''}`;
    openDialog($('[data-tb-dialog]'), opener);
  }

  /* ----------------------------------------------------------------- import */
  function importText(text) {
    const byCode = new Map();
    state.cards.forEach((c) => {
      const code = norm(c.c) + (c.b === 'signature' ? ' signature' : '');
      if (!byCode.has(code)) byCode.set(code, c);
    });
    const byName = new Map(state.cards.map((c) => [norm(c.n), c]));
    let count = 0;
    const missing = [];
    text.split(/\r?\n/).forEach((raw) => {
      const line = raw.replace(/#.*$/, '').trim();
      const m = line.match(/^(\d+)\s*[x×]?\s+(.+?)(?:\s+(NM|EX|GD|PL))?$/i);
      if (!m) return;
      const q = norm(m[2]).replace(/\s+/g, ' ');
      const card = byCode.get(q) || byCode.get(q.replace(/\s+signature$/, '')) || byName.get(q)
        || state.cards.find((c) => norm(c.n).startsWith(q));
      if (!card) { missing.push(m[2]); return; }
      const key = `${card.k}|${(m[3] || 'NM').toUpperCase()}`;
      state.store.items[key] = (state.store.items[key] || 0) + parseInt(m[1], 10);
      count += parseInt(m[1], 10);
    });
    saveStore();
    applyFilters();
    renderBinder();
    return { count, missing };
  }

  /* ----------------------------------------------------------------- events */
  const list = $('[data-tb-list]');
  list.dataset.emptyHtml = list.innerHTML;

  $('[data-tb-grid]').addEventListener('click', (e) => {
    const tile = e.target.closest('.tb-tile');
    if (!tile) return;
    if (e.target.closest('[data-inc]')) change(tile.dataset.k, 'NM', 1);
    else if (e.target.closest('[data-dec]')) decrement(tile.dataset.k);
  });

  list.addEventListener('click', (e) => {
    const li = e.target.closest('.tb-line');
    if (!li) return;
    const [k, cond] = li.dataset.key.split('|');
    if (e.target.closest('[data-line-inc]')) change(k, cond, 1);
    else if (e.target.closest('[data-line-dec]')) change(k, cond, -1);
  });
  list.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-cond]');
    if (sel) setCondition(sel.closest('.tb-line').dataset.key, sel.value);
  });

  $('[data-tb-search]').addEventListener('input', debounce((e) => { state.f.q = e.target.value; applyFilters(); }, 150));
  $$('[data-tb-filter]').forEach((sel) => sel.addEventListener('change', () => { state.f[sel.dataset.tbFilter] = sel.value; applyFilters(); }));
  $('[data-tb-sort]').addEventListener('change', (e) => { state.sort = e.target.value; applyFilters(); });
  $('[data-tb-mine]').addEventListener('change', (e) => { state.f.mine = e.target.checked; applyFilters(); });
  $('[data-tb-retry]').addEventListener('click', load);

  $$('[data-tb-payout]').forEach((r) => r.addEventListener('change', () => {
    state.store.payout = r.value;
    saveStore();
    renderBinder();
    applyFilters(); // tile prices follow the chosen payout
  }));

  $('[data-tb-send]').addEventListener('click', (e) => openSend(e.currentTarget));

  $('[data-tb-clear]').addEventListener('click', () => {
    const backup = { ...state.store.items };
    if (!Object.keys(backup).length) return;
    state.store.items = {};
    saveStore();
    applyFilters();
    renderBinder();
    const region = document.querySelector('[data-toast-region]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML = `<span>${esc(S.cleared)}</span><a href="#" role="button">${esc(S.undo)}</a>`;
    el.querySelector('a').addEventListener('click', (ev) => {
      ev.preventDefault();
      state.store.items = backup;
      saveStore();
      applyFilters();
      renderBinder();
      el.remove();
    });
    region.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  });

  async function copy(text) {
    try { await navigator.clipboard.writeText(text); return true; } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      const ok = document.execCommand('copy'); ta.remove(); return ok;
    }
  }
  $('[data-tb-export]').addEventListener('click', async () => { if (await copy(exportText())) toast(S.copied, 'success'); });

  const importDialog = $('[data-tb-import]');
  $('[data-tb-import-open]').addEventListener('click', (e) => { $('[data-tb-import-result]').textContent = ''; openDialog(importDialog, e.currentTarget); });
  $('[data-tb-import-run]').addEventListener('click', () => {
    const res = importText($('[data-tb-import-text]').value);
    $('[data-tb-import-result]').textContent = fmt(S.importDone, { count: res.count }) + (res.missing.length ? ` ${fmt(S.importMissing, { cards: res.missing.slice(0, 8).join(', ') })}` : '');
    if (!res.missing.length) setTimeout(() => importDialog.close(), 700);
  });

  // Mobile binder sheet (same bottom sheet as the deck builder)
  $('[data-tb-open]').addEventListener('click', () => { root.classList.add('is-deck-open'); document.body.classList.add('is-locked'); $('[data-tb-close]').focus({ preventScroll: true }); });
  $('[data-tb-close]').addEventListener('click', () => { root.classList.remove('is-deck-open'); document.body.classList.remove('is-locked'); $('[data-tb-open]').focus(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.classList.contains('is-deck-open')) $('[data-tb-close]').click(); });

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && state.rendered < state.filtered.length) renderMore();
    }, { rootMargin: '800px 0px' }).observe($('[data-tb-sentinel]'));
  }

  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    loadStore();
    applyFilters();
    renderBinder();
  });

  /* ------------------------------------------------------- after submission */
  loadStore();
  mergeAccountBinder();
  // hexdeck.net/pages/sell-your-cards#my-binder opens with only the customer's own cards shown
  function showMine() {
    if (!/^#my-binder$/.test(window.location.hash)) return false;
    state.f.mine = true;
    $('[data-tb-mine]').checked = true;
    return true;
  }
  showMine();
  window.addEventListener('hashchange', () => { if (showMine() && state.cards.length) applyFilters(); });
  if ($('[data-tb-posted]')) {
    // Shopify reloaded the page after a successful send: keep the cards, show the reference.
    $('[data-tb-sent]').hidden = false;
    $('[data-tb-sent-ref]').textContent = state.store.pendingRef ? `(${state.store.pendingRef})` : '';
    state.store.lastSent = { ref: state.store.pendingRef, at: Date.now(), text: state.store.pendingOffer || '', data: state.store.pendingData || null };
    delete state.store.pendingRef;
    delete state.store.pendingOffer;
    delete state.store.pendingData;
    saveStore();
    $('[data-tb-sent]').scrollIntoView({ block: 'center' });
  } else if ($('[data-tb-form-error]')) {
    // Validation error from Shopify: reopen the form so the customer sees it.
    const dialog = $('[data-tb-dialog]');
    const t = setInterval(() => { if (state.cards.length) { clearInterval(t); openSend(); } }, 200);
    setTimeout(() => clearInterval(t), 10000);
    if (!dialog) clearInterval(t);
  }
  // The seller's details from the send form go on the receipt too (saved right before the page reloads).
  const sendForm = $('#TbName') && $('#TbName').form;
  if (sendForm) sendForm.addEventListener('submit', () => {
    if (!state.store.pendingData) return;
    const val = (n) => (sendForm.elements[n] ? sendForm.elements[n].value.trim() : '');
    state.store.pendingData.seller = { name: val('contact[name]'), email: val('contact[email]'), phone: val('contact[phone]'), delivery: val('contact[Delivery]'), notes: val('contact[Notes]') };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.store)); } catch (_) { /* ignore */ }
  });

  // Printable receipt (A4): what the customer offered, what Hexdeck pays and what happens next.
  function receiptHTML(sent) {
    const d = sent.data;
    const co = root.dataset;
    const date = new Date(d.at || sent.at || Date.now());
    const valid = new Date(date.getTime() + 7 * 864e5);
    const day = (x) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const condName = { NM: 'Near Mint', EX: 'Excellent', GD: 'Good', PL: 'Played' };
    const kindName = { signature: 'Signature', overnumbered: 'Overnumbered', standard: '' };
    const total = d.credit ? d.creditTotal : d.cash;
    const seller = d.seller || {};
    const rows = d.lines.map((l, i) => `
      <tr>
        <td class="n">${i + 1}</td>
        <td><div class="card">${l.img ? `<img src="${esc(l.img)}" alt="">` : '<span class="ph"></span>'}
          <div><b>${esc(l.name)}</b><span>${esc(l.code)}${kindName[l.kind] ? ` · ${kindName[l.kind]}` : ''}</span></div></div></td>
        <td>${esc(condName[l.cond] || l.cond)}</td>
        <td class="r">${l.qty}</td>
        <td class="r">${l.unit === null ? '<i>on request</i>' : euro(l.unit)}</td>
        <td class="r"><b>${l.unit === null ? '–' : euro(l.unit * l.qty)}</b></td>
      </tr>`).join('');
    return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Hexdeck sell offer ${esc(sent.ref || '')}</title>
<style>
  @page { size: A4; margin: 14mm 14mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font: 12px/1.5 "Segoe UI", system-ui, -apple-system, Arial, sans-serif; color: #14202b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { max-width: 780px; margin: 0 auto; padding: 24px; }
  header { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-radius: 12px; background: #04121c; color: #fff; }
  header img { height: 34px; }
  header .t { text-align: right; }
  header .t b { display: block; font-size: 18px; letter-spacing: .02em; color: #fdcf58; }
  header .t span { font-size: 11px; color: #b9c7d2; }
  .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
  .meta div { padding: 10px 12px; border: 1px solid #dfe5ea; border-radius: 10px; }
  .meta span { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #6b7a86; }
  .meta b { font-size: 13px; }
  .meta .total { background: #fff8e1; border-color: #f3d488; }
  .meta .total b { font-size: 17px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
  .parties div { padding: 12px 14px; background: #f4f7f9; border-radius: 10px; }
  .parties h3, h2 { margin: 0 0 6px; font-size: 10px; text-transform: uppercase; letter-spacing: .1em; color: #6b7a86; }
  .parties p { margin: 0; }
  table { width: 100%; border-collapse: collapse; }
  thead th { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #6b7a86; text-align: left; padding: 8px 6px; border-bottom: 2px solid #14202b; }
  tbody td { padding: 7px 6px; border-bottom: 1px solid #e6ebef; vertical-align: middle; }
  tr { page-break-inside: avoid; }
  .n { color: #8a97a1; width: 26px; }
  .r { text-align: right; white-space: nowrap; }
  th.r { text-align: right; }
  .card { display: flex; align-items: center; gap: 10px; }
  .card img, .card .ph { width: 26px; height: 36px; object-fit: cover; border-radius: 3px; background: #e6ebef; flex-shrink: 0; }
  .card span { display: block; font-size: 10.5px; color: #6b7a86; }
  .sum { margin: 12px 0 18px auto; width: 300px; }
  .sum div { display: flex; justify-content: space-between; padding: 5px 0; }
  .sum .grand { border-top: 2px solid #14202b; margin-top: 4px; padding-top: 8px; font-size: 15px; font-weight: 700; }
  .steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 6px 0 14px; page-break-inside: avoid; }
  .steps div { padding: 10px 12px; border: 1px solid #dfe5ea; border-radius: 10px; }
  .steps b { display: inline-grid; place-items: center; width: 20px; height: 20px; border-radius: 50%; background: #fdcf58; color: #14202b; font-size: 11px; margin-bottom: 4px; }
  .notes { font-size: 10.5px; color: #4d5b66; page-break-inside: avoid; }
  .notes li { margin-bottom: 3px; }
  footer { margin-top: 18px; padding-top: 10px; border-top: 1px solid #dfe5ea; font-size: 10px; color: #6b7a86; display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  @media print { .wrap { padding: 0; } }
</style></head><body><div class="wrap">
  <header>
    <img src="${esc(co.logo)}" alt="${esc(co.coName)}">
    <div class="t"><b>SELL OFFER</b><span>Reference ${esc(sent.ref || d.ref)}</span></div>
  </header>

  <section class="meta">
    <div><span>Date</span><b>${day(date)}</b></div>
    <div><span>Valid until</span><b>${day(valid)}</b></div>
    <div><span>Payout</span><b>${d.credit ? `Store credit (+${d.bonus}%)` : 'Bank transfer'}</b></div>
    <div class="total"><span>Offer total</span><b>${euro(total)}</b></div>
  </section>

  <section class="parties">
    <div><h3>Seller</h3><p>${seller.name ? `<b>${esc(seller.name)}</b><br>` : ''}${esc(seller.email || '')}${seller.phone ? `<br>${esc(seller.phone)}` : ''}${seller.delivery ? `<br>Delivery: ${esc(seller.delivery)}` : ''}</p></div>
    <div><h3>Buyer</h3><p><b>${esc(co.coName)}</b><br>${esc(co.coAddress)}<br>${esc(co.coEmail)}</p></div>
  </section>

  <h2>Cards (${d.units})</h2>
  <table>
    <thead><tr><th>#</th><th>Card</th><th>Condition</th><th class="r">Qty</th><th class="r">Price each</th><th class="r">Total</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="sum">
    <div><span>Cash value</span><span>${euro(d.cash)}</span></div>
    ${d.credit ? `<div><span>Store credit bonus (+${d.bonus}%)</span><span>${euro(d.creditTotal - d.cash)}</span></div>` : ''}
    ${d.onRequest ? `<div><span>Cards priced on request</span><span>${d.onRequest}</span></div>` : ''}
    <div class="grand"><span>${d.credit ? 'Total store credit' : 'Total payout'}</span><span>${euro(total)}</span></div>
  </div>

  <h2>What happens next</h2>
  <section class="steps">
    <div><b>1</b><br>We check your offer and confirm by email within 2 working days.</div>
    <div><b>2</b><br>Pack each card in a sleeve and toploader. Put this sheet or reference <strong>${esc(sent.ref || d.ref)}</strong> in the parcel.</div>
    <div><b>3</b><br>Send the parcel (tracked) to the address we confirm, or drop it off in Rotterdam by appointment.</div>
    <div><b>4</b><br>After checking the cards we pay within 3 working days${d.credit ? ' as a Hexdeck gift card' : ' by bank transfer'}.</div>
  </section>

  <ul class="notes">
    <li>Prices are based on the market price on the date above: ${d.pct.standard}% for regular cards, ${d.pct.overnumbered}% for overnumbered and ${d.pct.signature}% for signature cards; Excellent −${d.cond.EX}%, Good −${d.cond.GD}%, Played −${d.cond.PL}%.</li>
    <li>The final price is set after we have checked the cards. If a card's condition differs from what you selected we adjust the price for that card and tell you before paying; you can then have it returned.</li>
    <li>This offer is valid for 7 days. By sending the cards you confirm they are yours and genuine. Full terms: ${esc(co.coWeb)}/policies/terms-of-service.</li>
  </ul>

  <footer>
    <span>${esc(co.coName)} · ${esc(co.coAddress)}</span>
    <span>${co.coKvk ? `KvK ${esc(co.coKvk)} · ` : ''}${co.coVat ? `VAT ${esc(co.coVat)} · ` : ''}${esc(co.coEmail)} · ${esc(co.coWeb)}</span>
  </footer>
</div>
<script>window.addEventListener('load', function () { setTimeout(function () { window.focus(); window.print(); }, 150); });<\/script>
</body></html>`;
  }

  // Receipt for the customer: the offer exactly as it was sent, ready to print or save as PDF.
  const printBtn = $('[data-tb-print-offer]');
  if (printBtn) printBtn.addEventListener('click', () => {
    const sent = state.store.lastSent || {};
    const w = window.open('', '_blank');
    if (!w) return;
    if (sent.data) {
      w.document.write(receiptHTML(sent)); // prints itself once the logo and card images are loaded
      w.document.close();
      return;
    }
    // Offers sent before the new receipt existed only have the plain text.
    w.document.write(`<!doctype html><meta charset="utf-8"><title>${esc(sent.ref || 'Hexdeck offer')}</title>
      <style>body{font:14px/1.5 system-ui,sans-serif;margin:32px;color:#111}h1{font-size:20px;margin:0 0 4px}p{margin:0 0 16px;color:#555}pre{white-space:pre-wrap;font:13px/1.6 ui-monospace,Consolas,monospace}</style>
      <h1>Hexdeck – ${esc(S.sentTitle || 'Offer')}</h1><p>${new Date(sent.at || Date.now()).toLocaleString()}</p><pre>${esc(sent.text || '')}</pre>`);
    w.document.close();
    w.focus();
    w.print();
  });

  $('[data-tb-new-binder]').addEventListener('click', () => {
    state.store.items = {};
    saveStore();
    $('[data-tb-sent]').hidden = true;
    applyFilters();
    renderBinder();
  });

  renderBinder();
  load();
})();
